import { createContext, useCallback, useMemo, useState } from 'react';

import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';

import { ONE_ON_ONE_STATUSES } from 'constants/ProjectConstants';
import { sendClickedOnRescheduleEvent } from 'constants/mixpanelHelper';

import { getCheckInTimeline } from 'services/OneOnOne';
import { showErrorSnackbar } from 'services/snackbar';
import { updateCheckInDateTime } from 'services/task';

const getOtherPerson = (employees, employeeId) => employees.filter((employee) => employee.id !== employeeId);

const defaultContext = {
  instanceDetails: null,
  otherMember: null,
  loggedInEmployee: null,
  comments: [],
  isInstanceEditable: true,
};

export const OneOnOneInstanceContext = createContext(defaultContext);

export const OneOnOneInstanceContextProvider = ({ children }) => {
  const dispatch = useDispatch();
  const [instanceDetails, setInstanceDetails] = useState(null);
  const [currentTimeline, setcurrentTimeline] = useState(null);
  const [comments, setComments] = useState([]);
  const [timelineData, setTimelineData] = useState([]);
  const [mappedTimelineData, setMappedTimelineData] = useState([]);

  const [endMeetingModal, setEndMeetingModal] = useState(false);
  const [cancelMeetingModal, setcancelMeetingModal] = useState(false);

  const loggedInEmployee = useSelector(selectLoggedInEmployeeData, shallowEqual);

  const reportee = useMemo(() => {
    if (!instanceDetails) return null;
    return getOtherPerson(instanceDetails.toIds, instanceDetails.from.id)[0];
  }, [instanceDetails, loggedInEmployee]);

  const otherEmployee = useMemo(() => {
    if (!instanceDetails) return null;

    return getOtherPerson(instanceDetails.toIds, loggedInEmployee.id)[0];
  }, [instanceDetails, loggedInEmployee]);

  const updateOneOnOne = (updatedOneOnOne) => {
    setInstanceDetails((prev) => ({ ...prev, ...updatedOneOnOne }));
  };

  const updateInstanceDetails = (details) => {
    setInstanceDetails(details);

    getCheckInTimeline({ checkInId: details.parentTaskId, offset: 0, limit: 100 })
      .then((res) => {
        const tLData = res.filter((dataObj) => dataObj.status !== ONE_ON_ONE_STATUSES.DISABLED);
        const mappedTLData = tLData.map((dataObj) => ({
          instanceId: dataObj.id,
          dueTS: dataObj.dueTS,
          createdTS: dataObj.createdTS,
        }));
        const currentTimeline = res.find((timeline) => timeline.id?.toString() === details.id);
        setcurrentTimeline(currentTimeline);
        setTimelineData(tLData);
        setMappedTimelineData(mappedTLData);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  const toggleEndMeetingModal = useCallback(() => {
    setEndMeetingModal((prevVal) => !prevVal);
  }, []);

  const toggleCancelMeetingModal = useCallback(() => {
    setcancelMeetingModal((prevVal) => !prevVal);
  }, []);

  const updateDetailedTimeline = (checkInId, checkInState) => {
    const updatedTimelineData = timelineData.map((timeline) =>
      timeline.id === parseInt(checkInId) ? { ...timeline, status: checkInState } : timeline
    );

    setTimelineData(updatedTimelineData);
  };

  const dateTimeChangeHandler = async (newStartDateTime, newDueDateTime) => {
    if (!newStartDateTime || !newDueDateTime) {
      dispatch(showErrorSnackbar('Please select a date for 1:1'));
      return;
    }

    try {
      const res = await updateCheckInDateTime({
        checkInInstanceId: instanceDetails.id,
        dueDate: newDueDateTime.getTime(),
        startDate: newStartDateTime.getTime(),
      });

      updateOneOnOne(res);
      sendClickedOnRescheduleEvent({
        eventProperties: {
          role: loggedInEmployee.userRole,
        },
      });
    } catch (err) {
      dispatch(showErrorSnackbar(err.message || 'Request Failed: Please check your connection'));
      console.error(err);
    }
  };

  return (
    <OneOnOneInstanceContext.Provider
      value={{
        state: {
          instanceDetails,
          otherEmployee,
          loggedInEmployee,
          comments,
          timelineData: mappedTimelineData,
          detailedTimeLineData: timelineData,
          reportee,
          endMeetingModal,
          cancelMeetingModal,
          currentTimeline,
        },
        updateInstanceDetails,
        updateOneOnOne,
        setComments,
        toggleCancelMeetingModal,
        toggleEndMeetingModal,
        dateTimeChangeHandler,
        updateDetailedTimeline,
      }}
    >
      {children}
    </OneOnOneInstanceContext.Provider>
  );
};
